import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, GripVertical, ChevronDown, ChevronUp, Globe, LogOut } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to send credentials (cookies) with requests
const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

const FAQAdminPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedLangs, setExpandedLangs] = useState({});
  const [formData, setFormData] = useState({
    question_nl: '',
    answer_nl: '',
    question_en: '',
    answer_en: '',
    question_fr: '',
    answer_fr: '',
    question_de: '',
    answer_de: '',
    order: 0,
  });

  const { getAuthHeaders, logout, user } = useAuth();
  const navigate = useNavigate();

  const languages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await api.get('/api/faq?active_only=false');
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultFAQs = async () => {
    try {
      await api.post('/api/faq/seed');
      fetchFAQs();
    } catch (error) {
      console.error('Error seeding FAQs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/faq/${editingId}`, formData);
      } else {
        await api.post('/api/faq', formData);
      }
      resetForm();
      fetchFAQs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    }
  };

  const handleEdit = (faq) => {
    setFormData({
      question_nl: faq.question_nl || '',
      answer_nl: faq.answer_nl || '',
      question_en: faq.question_en || '',
      answer_en: faq.answer_en || '',
      question_fr: faq.question_fr || '',
      answer_fr: faq.answer_fr || '',
      question_de: faq.question_de || '',
      answer_de: faq.answer_de || '',
      order: faq.order || 0,
    });
    setEditingId(faq.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Weet je zeker dat je deze vraag wilt verwijderen?')) {
      try {
        await api.delete(`/api/faq/${id}`);
        fetchFAQs();
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        if (error.response?.status === 401) {
          logout();
          navigate('/admin/login');
        }
      }
    }
  };

  const toggleActive = async (faq) => {
    try {
      await api.put(`/api/faq/${faq.id}`, {
        is_active: !faq.is_active,
      });
      fetchFAQs();
    } catch (error) {
      console.error('Error toggling FAQ:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      question_nl: '',
      answer_nl: '',
      question_en: '',
      answer_en: '',
      question_fr: '',
      answer_fr: '',
      question_de: '',
      answer_de: '',
      order: 0,
    });
    setEditingId(null);
    setShowAddForm(false);
    setExpandedLangs({});
  };

  const toggleLangExpand = (langCode) => {
    setExpandedLangs(prev => ({
      ...prev,
      [langCode]: !prev[langCode]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fc] pt-32 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#f8f9fc] pt-32 pb-20" data-testid="faq-admin-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a]">FAQ Beheer</h1>
            <p className="text-[#475569] mt-1">Welkom, {user} • Beheer je veelgestelde vragen</p>
          </div>
          <div className="flex gap-3">
            {faqs.length === 0 && (
              <button
                onClick={seedDefaultFAQs}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors"
              >
                Laad voorbeelden
              </button>
            )}
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-accent flex items-center gap-2"
              data-testid="add-faq-button"
            >
              <Plus size={20} />
              Nieuwe vraag
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-300 transition-colors flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              Uitloggen
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0f172a]">
                  {editingId ? 'Vraag bewerken' : 'Nieuwe vraag toevoegen'}
                </h2>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dutch (required) */}
                <div className="space-y-4 p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-[#0f172a] font-semibold">
                    <span className="text-xl">🇳🇱</span>
                    Nederlands (verplicht)
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-2">
                      Vraag *
                    </label>
                    <input
                      type="text"
                      value={formData.question_nl}
                      onChange={(e) => setFormData({ ...formData, question_nl: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
                      placeholder="Typ hier de vraag..."
                      data-testid="input-question-nl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0f172a] mb-2">
                      Antwoord *
                    </label>
                    <textarea
                      value={formData.answer_nl}
                      onChange={(e) => setFormData({ ...formData, answer_nl: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 resize-none"
                      placeholder="Typ hier het antwoord..."
                      data-testid="input-answer-nl"
                    />
                  </div>
                </div>

                {/* Other languages (expandable) */}
                {languages.slice(1).map((lang) => (
                  <div key={lang.code} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleLangExpand(lang.code)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2 font-medium text-[#0f172a]">
                        <span className="text-xl">{lang.flag}</span>
                        {lang.name} (optioneel)
                      </div>
                      {expandedLangs[lang.code] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    <AnimatePresence>
                      {expandedLangs[lang.code] && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-[#0f172a] mb-2">
                                Vraag ({lang.name})
                              </label>
                              <input
                                type="text"
                                value={formData[`question_${lang.code}`]}
                                onChange={(e) => setFormData({ ...formData, [`question_${lang.code}`]: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB]"
                                placeholder={`Question in ${lang.name}...`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[#0f172a] mb-2">
                                Antwoord ({lang.name})
                              </label>
                              <textarea
                                value={formData[`answer_${lang.code}`]}
                                onChange={(e) => setFormData({ ...formData, [`answer_${lang.code}`]: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB] resize-none"
                                placeholder={`Answer in ${lang.name}...`}
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-[#0f172a] mb-2">
                    Volgorde (lager = hoger in lijst)
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-32 px-4 py-3 rounded-xl border border-slate-200 focus:border-[#2563EB]"
                    min="0"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="btn-accent flex items-center gap-2"
                    data-testid="save-faq-button"
                  >
                    <Save size={20} />
                    {editingId ? 'Opslaan' : 'Toevoegen'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300 transition-colors"
                  >
                    Annuleren
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#0f172a] mb-2">Nog geen FAQ's</h3>
              <p className="text-[#475569] mb-6">
                Voeg je eerste vraag toe of laad de voorbeeldvragen.
              </p>
              <button
                onClick={seedDefaultFAQs}
                className="btn-primary"
              >
                Laad voorbeeldvragen
              </button>
            </div>
          ) : (
            faqs.map((faq, index) => (
              <motion.div
                key={faq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl border shadow-sm p-6 ${
                  faq.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50/50'
                }`}
                data-testid={`faq-item-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 text-slate-400 cursor-grab">
                    <GripVertical size={20} />
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-mono text-slate-400">#{faq.order}</span>
                          {!faq.is_active && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                              Inactief
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-[#0f172a] mb-2">
                          {faq.question_nl}
                        </h3>
                        <p className="text-[#475569] text-sm line-clamp-2">
                          {faq.answer_nl}
                        </p>
                        <div className="flex gap-2 mt-3">
                          {faq.question_en && <span className="text-xs bg-slate-100 px-2 py-1 rounded">🇬🇧 EN</span>}
                          {faq.question_fr && <span className="text-xs bg-slate-100 px-2 py-1 rounded">🇫🇷 FR</span>}
                          {faq.question_de && <span className="text-xs bg-slate-100 px-2 py-1 rounded">🇩🇪 DE</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => toggleActive(faq)}
                          className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                            faq.is_active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {faq.is_active ? 'Actief' : 'Inactief'}
                        </button>
                        <button
                          onClick={() => handleEdit(faq)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                          data-testid={`edit-faq-${index}`}
                        >
                          <Edit2 size={18} className="text-[#2563EB]" />
                        </button>
                        <button
                          onClick={() => handleDelete(faq.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-faq-${index}`}
                        >
                          <Trash2 size={18} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </main>
  );
};

export default FAQAdminPage;
